/**
 * What to draw for the wall graph (PRP-SKETCH-FIELD-3 J15, J9; item 4). Shared by
 * the phone Sketch screen (PlanLayer) and the web plan (FloorPlanView), so both
 * draw the same plan the same way. Pure: points in plan feet, no styling.
 *
 * - `segmentBands`: the half of a segment's thickness on each side that has a room,
 *   filled with room fill (FIELD-2 E19: the strip between two rooms is room fill).
 * - `segmentRuns`: the wall line runs along a segment's centerline, minus the
 *   stretches its openings take.
 * - `openingGlyph`: the symbol for one opening on its graph segment: jambs across
 *   the wall, a window's three lines, a missing wall's dotted line, and the door
 *   kinds per `app/services/placement/door_glyph.py`: a swing's leaf and arc;
 *   French, two leaves; a bifold, one V (two for a double) on the side it opens to;
 *   a bypass or sliding door, two panels along the wall overlapping in the middle
 *   (four for a double bypass); a pocket door, its panel in the wall slid toward
 *   its pocket. The door kind is read through `esx_door_type` (Xactimate's
 *   doorType: 1 bifold, 2 sliding, 4 pocket, 0 swing) and `esx_double_door`.
 */
import { DOOR_ANGLE_RAD, POLICY, WALL_FT } from './policy';
import { EPS, dist, findRoom, unit } from './geom';
import { segMinus } from './shared';
import { PANELS_DOUBLE, alwaysDouble } from './doors';
import type { GraphOpening, GraphSegment } from './graph';
import type { Opening, Plan, Pt } from './types';

/** Xactimate doorType values (FIF DoorTypeID; Decision-0020). */
const DOOR_BIFOLD = 1;
const DOOR_SLIDING = 2;
const DOOR_POCKET = 4; // policy-literal-ok: Xactimate doorType id (Decision-0020), compared with esx_door_type

const add = (p: Pt, d: Pt, k: number): Pt => ({ x: p.x + d.x * k, y: p.y + d.y * k });
const mid = (a: Pt, b: Pt): Pt => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
const G = () => POLICY.screen.door_glyph;

export type Seg2 = [Pt, Pt];
/** An arc of radius `r` around `c` from `from` to `to`; `sweep` is the SVG sweep flag (y-down). */
export interface Arc { c: Pt; from: Pt; to: Pt; r: number; sweep: 0 | 1 }

export interface Glyph {
  /** Solid strokes: jambs, door leaves, window lines, sliding and pocket panels. */
  lines: Seg2[];
  /** Dashed swing arcs. */
  arcs: Arc[];
  /** A missing wall's dotted line. */
  dotted: Seg2[];
  /** The window's glass band (filled), as a polygon. */
  band: Pt[] | null;
}

/** A segment's line runs (feet from `a`), its openings' stretches removed. */
export function segmentRuns(g: GraphSegment): [number, number][] {
  return segMinus([[0, dist(g.a, g.b)]], g.cuts);
}

/** A run along segment `g` offset `k` feet along its normal `N`. */
export function runPts(g: GraphSegment, [s0, s1]: [number, number], k = 0): Seg2 {
  return [add(add(g.a, g.u, s0), g.N, k), add(add(g.a, g.u, s1), g.N, k)];
}

/** The outer-face offset (feet along `N`) of a segment's outer line, or null. */
export function outerOffset(g: GraphSegment): number | null {
  if (!g.outer) return null;
  const o = g.outer[0];
  return (o.x - g.a.x) * g.N.x + (o.y - g.a.y) * g.N.y;
}

/** The half-thickness strip on each side of segment `g` that has a room, with that room's id. */
export function segmentBands(g: GraphSegment): { rid: string; pts: Pt[] }[] {
  if (g.thickFt < EPS) return [];
  const out: { rid: string; pts: Pt[] }[] = [];
  const half = g.thickFt / 2;
  const side = (rid: string | undefined, k: number) => {
    if (!rid) return;
    out.push({ rid, pts: [g.a, g.b, add(g.b, g.N, k * half), add(g.a, g.N, k * half)] });
  };
  side(g.plus[0], 1);
  side(g.minus[0], -1);
  return out;
}

/** SVG sweep flag for an arc around `c` from `p` to `q` (the short way). */
function sweepOf(c: Pt, p: Pt, q: Pt): 0 | 1 {
  const cr = (p.x - c.x) * (q.y - c.y) - (p.y - c.y) * (q.x - c.x);
  return cr > 0 ? 1 : 0;
}

/** A leaf of length `len` from hinge `h`, along `d` turned toward `s` by `ang`, and its arc from the closed position. */
function leaf(h: Pt, d: Pt, s: Pt, len: number, ang: number): { line: Seg2; arc: Arc } {
  const v = { x: d.x * Math.cos(ang) + s.x * Math.sin(ang), y: d.y * Math.cos(ang) + s.y * Math.sin(ang) };
  const tip = add(h, v, len), shut = add(h, d, len);
  return { line: [h, tip], arc: { c: h, from: shut, to: tip, r: len, sweep: sweepOf(h, shut, tip) } };
}

/** A bifold's V between `p` and `q` (on the face), its peak toward `s`. */
function vee(p: Pt, q: Pt, s: Pt): Seg2[] {
  const half = dist(p, q) / 2;
  const peak = add(mid(p, q), s, half * Math.tan((G().bifold_fold_deg * Math.PI) / 180));
  return [[p, peak], [peak, q]];
}

function doorGlyph(o: Opening, go: GraphOpening, g: GraphSegment, half: number, out: Glyph): void {
  const geom = go.geom;
  if (!geom?.hinge || !geom.side) return;
  const type = o.door?.type ?? POLICY.door_default_interior.type;
  const xt = POLICY.esx_door_type[type] ?? 0;
  const double = alwaysDouble(type) || o.door?.panels === PANELS_DOUBLE;
  const s = geom.side, w = geom.w;
  const h0 = geom.hinge;
  const d = { x: Math.cos(geom.aJ ?? 0), y: Math.sin(geom.aJ ?? 0) };
  const h1 = add(h0, d, w);
  const ang = DOOR_ANGLE_RAD;
  if (xt === DOOR_BIFOLD) {
    // The V opens to the door's swing side (the sheet's bifold side sets `out`).
    if (double) out.lines.push(...vee(h0, mid(h0, h1), s), ...vee(mid(h0, h1), h1, s));
    else out.lines.push(...vee(h0, h1, s));
    return;
  }
  if (xt === DOOR_SLIDING || xt === DOOR_POCKET) {
    const c0 = go.p0, c1 = go.p1, u = unit(c0, c1), W = dist(c0, c1);
    if (xt === DOOR_POCKET) {
      // Slid toward the hinge end (single) or both ends (double), inside the wall.
      const k = G().pocket_open_w;
      const hingeAtC0 = dist(h0, c0) <= dist(h0, c1);
      if (double) {
        out.lines.push([add(c0, u, -k * W / 2), add(c0, u, (1 - k) * W / 2)], [add(c0, u, W / 2 + k * W / 2), add(c1, u, k * W / 2)]);
      } else {
        const sh = (hingeAtC0 ? -1 : 1) * k * W;
        out.lines.push([add(c0, u, sh), add(c1, u, sh)]);
      }
      return;
    }
    const off = half * G().slide_offset_t * 2;
    const pair = (a: number, b: number) => {
      const m = (a + b) / 2, ov = (b - a) * G().slide_overlap_w;
      out.lines.push(
        [add(add(c0, u, a), g.N, off), add(add(c0, u, m + ov), g.N, off)],
        [add(add(c0, u, m - ov), g.N, -off), add(add(c0, u, b), g.N, -off)],
      );
    };
    if (double) { pair(0, W / 2); pair(W / 2, W); } else pair(0, W);
    return;
  }
  if (double) {
    for (const [h, dd] of [[h0, d], [h1, { x: -d.x, y: -d.y }]] as [Pt, Pt][]) {
      const l = leaf(h, dd, s, w / 2, ang);
      out.lines.push(l.line);
      out.arcs.push(l.arc);
    }
    return;
  }
  const l = leaf(h0, d, s, w, ang);
  out.lines.push(l.line);
  out.arcs.push(l.arc);
}

/** The symbol for graph opening `go` on its segment (J9, J15). */
export function openingGlyph(plan: Plan, segments: GraphSegment[], go: GraphOpening): Glyph {
  const out: Glyph = { lines: [], arcs: [], dotted: [], band: null };
  const g = segments[go.seg];
  const o = findRoom(plan, go.rid)?.openings.find((q) => q.id === go.oid);
  if (!g || !o) return out;
  const half = g.thickFt > EPS ? g.thickFt / 2 : WALL_FT * G().jamb_half_wall;
  const jambs = () => {
    for (const p of [go.p0, go.p1]) out.lines.push([add(p, g.N, -half), add(p, g.N, half)]);
  };
  if (o.kind === 'missing') {
    // W4: on a wall that absorbed a removed wall, the dotted line is on that wall's face.
    const k = g.face ?? 0;
    out.dotted.push([add(go.p0, g.N, k), add(go.p1, g.N, k)]);
    if (o.missing && o.missing.trim !== POLICY.missing_wall_default.trim) jambs();
    return out;
  }
  jambs();
  if (o.kind === 'window') {
    for (const k of [-half, 0, half]) out.lines.push([add(go.p0, g.N, k), add(go.p1, g.N, k)]);
    out.band = [add(go.p0, g.N, -half), add(go.p1, g.N, -half), add(go.p1, g.N, half), add(go.p0, g.N, half)];
    return out;
  }
  doorGlyph(o, go, g, half, out);
  return out;
}
